// ============================================================
// filterEngine.js — Photo Editor preset filters + adjustment math
// ------------------------------------------------------------
// Single source of truth for:
//   • ADJUSTMENTS    — the 14 slider definitions (label/icon/min/max/default)
//   • DEFAULT_SETTINGS — a fresh "Normal" settings object
//   • PRESET_FILTERS — named filter objects (every key from ADJUSTMENTS)
//   • buildCSSFilterString — exact CSS `filter` for canvas + accurate preview
//   • buildPreviewCSS — live-preview approximation (adds warmth/sepia)
//   • lerpSettings   — interpolate two settings objects (filter strength)
//
// Identity / persistence note: these are pure values — no Supabase here.
// ============================================================

// ── The 14 adjustment sliders (order = display order) ──────────────────
// key matches the keys used in every PRESET_FILTERS object below.
export const ADJUSTMENTS = [
  { key: 'brightness', label: 'Brightness', icon: 'sun',       min: 50,   max: 200, def: 100, unit: '%' },
  { key: 'contrast',   label: 'Contrast',   icon: 'contrast',  min: 0,    max: 200, def: 100, unit: '%' },
  { key: 'saturation', label: 'Saturation', icon: 'drop',      min: 0,    max: 200, def: 100, unit: '%' },
  { key: 'greyscale',  label: 'Greyscale',  icon: 'circle-half',min: 0,   max: 100, def: 0,   unit: '%' },
  { key: 'warmth',     label: 'Warmth',     icon: 'thermo',    min: -100, max: 100, def: 0,   unit: '',  signed: true },
  { key: 'highlights', label: 'Highlights', icon: 'sun-high',  min: -100, max: 100, def: 0,   unit: '',  signed: true },
  { key: 'shadows',    label: 'Shadows',    icon: 'moon',      min: -100, max: 100, def: 0,   unit: '',  signed: true },
  { key: 'fade',       label: 'Fade',       icon: 'fade',      min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'vignette',   label: 'Vignette',   icon: 'vignette',  min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'sharpen',    label: 'Sharpen',    icon: 'triangle',  min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'structure',  label: 'Structure',  icon: 'grid',      min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'tiltShift',  label: 'Tilt Shift', icon: 'aperture',  min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'grain',      label: 'Grain',      icon: 'noise',     min: 0,    max: 100, def: 0,   unit: '' },
  { key: 'hue',        label: 'Hue',        icon: 'palette',   min: -180, max: 180, def: 0,   unit: '°', signed: true },
];

// Fresh, all-neutral settings object (= the "Normal" preset).
export const DEFAULT_SETTINGS = ADJUSTMENTS.reduce((acc, a) => {
  acc[a.key] = a.def;
  return acc;
}, {});

// Helper so presets stay compact + always have every key.
const P = (overrides) => ({ ...DEFAULT_SETTINGS, ...overrides });

// ── Preset filters ─────────────────────────────────────────────────────
// Aesthetically distinct combinations. Values per the project spec.
export const PRESET_FILTERS = {
  Normal:        P({}),
  Clarendon:     P({ brightness:108, contrast:118, saturation:130, warmth:5,   highlights:10, shadows:-15, vignette:10, structure:10 }),
  Gingham:       P({ brightness:105, contrast:88,  saturation:78,  warmth:8,   highlights:5,  shadows:5,   fade:12, grain:5,  hue:-5 }),
  Moon:          P({ brightness:100, contrast:115, saturation:0,   greyscale:90, highlights:5, shadows:-10, fade:10, vignette:20, sharpen:5, grain:5 }),
  Lark:          P({ brightness:112, contrast:92,  saturation:82,  warmth:-10, highlights:15, shadows:10,  fade:5,  structure:5, hue:5 }),
  Reyes:         P({ brightness:108, contrast:82,  saturation:72,  warmth:18,  highlights:8,  shadows:8,   fade:22, grain:8,  hue:3 }),
  Juno:          P({ brightness:103, contrast:108, saturation:142, warmth:12,  shadows:-8,  vignette:5,  sharpen:8, structure:12, hue:-8 }),
  Slumber:       P({ brightness:86,  contrast:95,  saturation:58,  warmth:15,  highlights:-5, shadows:10,  fade:15, vignette:15, grain:12, hue:5 }),
  Ludwig:        P({ brightness:106, contrast:110, saturation:92,  warmth:6,   highlights:8,  shadows:-10, fade:6,  vignette:14, sharpen:6, structure:10 }),
  Perpetua:      P({ brightness:103, contrast:102, saturation:100, warmth:-8,  highlights:12, shadows:-5,  fade:5,  vignette:12, sharpen:5, structure:8, hue:-5 }),
  Paris:         P({ brightness:103, contrast:98,  saturation:88,  warmth:-8,  highlights:5,  shadows:5,   fade:18, vignette:5, grain:8, hue:2 }),
  Fade:          P({ brightness:107, contrast:82,  saturation:82,  highlights:5, shadows:5,   fade:25, grain:5 }),
  Vivid:         P({ brightness:105, contrast:118, saturation:145, warmth:5,   highlights:8,  shadows:-8,  vignette:8, sharpen:10, structure:15 }),
  Noir:          P({ brightness:95,  contrast:125, saturation:15,  greyscale:75, warmth:-5,  highlights:10, shadows:-15, fade:8, vignette:25, sharpen:8, structure:10, grain:15 }),
  Cool:          P({ brightness:103, contrast:102, saturation:105, warmth:-28, highlights:5,  fade:5,  vignette:5, hue:-8 }),
  Matte:         P({ brightness:100, contrast:82,  saturation:85,  warmth:5,   shadows:15,  fade:30, vignette:10, grain:12 }),
  Chrome:        P({ brightness:110, contrast:112, saturation:108, warmth:-5,  highlights:15, shadows:-12, sharpen:12, structure:18 }),
  Vintage:       P({ brightness:96,  contrast:90,  saturation:75,  warmth:20,  highlights:-5, shadows:10,  fade:20, vignette:30, grain:20, hue:5 }),
  Neon:          P({ brightness:105, contrast:115, saturation:180, highlights:15, shadows:-5, vignette:15, sharpen:10, structure:5, hue:20 }),
  'Los Angeles': P({ brightness:108, contrast:108, saturation:112, warmth:10,  highlights:8,  shadows:-5,  fade:5,  vignette:10, sharpen:5, structure:8, grain:5 }),
  // ── Extended set ──
  'Golden Hour': P({ brightness:106, contrast:104, saturation:120, warmth:35, highlights:12, shadows:5,   fade:8,  vignette:8,  hue:6 }),
  Dusk:          P({ brightness:96,  contrast:106, saturation:95,  warmth:-15, highlights:-5, shadows:8,   fade:10, vignette:18, hue:-15 }),
  Midnight:      P({ brightness:88,  contrast:120, saturation:80,  warmth:-25, highlights:-8, shadows:-12, vignette:28, sharpen:6, hue:-20 }),
  Forest:        P({ brightness:100, contrast:108, saturation:115, warmth:-5,  structure:12, vignette:10, hue:-12 }),
  Ocean:         P({ brightness:104, contrast:104, saturation:112, warmth:-22, highlights:8, vignette:8,  hue:12 }),
  Rose:          P({ brightness:107, contrast:96,  saturation:108, warmth:18,  highlights:10, fade:12, hue:8 }),
  Film:          P({ brightness:100, contrast:92,  saturation:82,  warmth:8,   shadows:10,   fade:22, vignette:14, grain:18, hue:3 }),
  Pastel:        P({ brightness:110, contrast:88,  saturation:78,  warmth:5,   highlights:15, fade:14, hue:4 }),
  Drama:         P({ brightness:98,  contrast:130, saturation:120, warmth:-3,  highlights:12, shadows:-18, vignette:22, sharpen:14, structure:18 }),
  Bronze:        P({ brightness:102, contrast:110, saturation:100, warmth:28,  highlights:6,  shadows:-6,  vignette:12, structure:10, hue:10 }),
  Sage:          P({ brightness:101, contrast:96,  saturation:72,  warmth:-6,  fade:16, vignette:8,  grain:6,  hue:-8 }),
  Blush:         P({ brightness:108, contrast:94,  saturation:98,  warmth:22,  highlights:12, fade:10, vignette:5,  hue:6 }),
};

// Ordered list of preset names for rendering the strip.
export const PRESET_NAMES = Object.keys(PRESET_FILTERS);

// ── CSS filter strings ─────────────────────────────────────────────────
// Exact, canvas-safe primitives. `ctx.filter` understands all of these,
// so the same string is used for the flattened export.
export function buildCSSFilterString(s = DEFAULT_SETTINGS) {
  return [
    `brightness(${s.brightness}%)`,
    `contrast(${s.contrast}%)`,
    `saturate(${s.saturation}%)`,
    `grayscale(${s.greyscale}%)`,
    `hue-rotate(${s.hue}deg)`,
  ].join(' ');
}

// Live-preview approximation. Adds warmth (sepia + hue nudge) on top of the
// canvas-safe primitives so the on-screen <img> looks closer to the export.
// Grain / tilt-shift are intentionally skipped in preview (export-only).
export function buildPreviewCSS(s = DEFAULT_SETTINGS) {
  const parts = [
    `brightness(${s.brightness}%)`,
    `contrast(${s.contrast}%)`,
    `saturate(${s.saturation}%)`,
    `grayscale(${s.greyscale}%)`,
    `hue-rotate(${s.hue}deg)`,
  ];
  // Warmth: positive = warmer (sepia), negative = cooler (blue hue shift).
  if (s.warmth > 0) {
    parts.push(`sepia(${Math.min(60, s.warmth * 0.6)}%)`);
  } else if (s.warmth < 0) {
    parts.push(`hue-rotate(${s.warmth * 0.4}deg)`);
    parts.push(`saturate(${100 + Math.abs(s.warmth) * 0.25}%)`);
  }
  // Sharpen: CSS can't truly sharpen, so approximate the "crisper" feel with a
  // little extra contrast + a touch less brightness. Real convolution runs on
  // export (canvasExport.applySharpen); this is just live visual feedback.
  if (s.sharpen > 0) {
    parts.push(`contrast(${100 + Math.min(15, s.sharpen * 0.13)}%)`);
    parts.push('brightness(98%)');
  }
  return parts.join(' ');
}

// ── Settings interpolation (filter strength 0..1) ──────────────────────
// strength 0 → a, strength 1 → b. Rounds to keep integer-ish slider values.
export function lerpSettings(a, b, strength) {
  const t = Math.max(0, Math.min(1, strength));
  const out = {};
  for (const { key } of ADJUSTMENTS) {
    const av = a[key] ?? 0;
    const bv = b[key] ?? 0;
    out[key] = Math.round(av + (bv - av) * t);
  }
  return out;
}

// Resolve the effective settings for a selected preset + strength.
// Strength interpolates between Normal and the preset.
export function resolvePresetSettings(presetName, strength = 1) {
  const preset = PRESET_FILTERS[presetName] || DEFAULT_SETTINGS;
  if (strength >= 1) return { ...preset };
  return lerpSettings(PRESET_FILTERS.Normal, preset, strength);
}

// True when a settings object is back to all-neutral.
export function isNeutral(s) {
  return ADJUSTMENTS.every(a => (s[a.key] ?? a.def) === a.def);
}

// Format a slider value for display (e.g. "+23", "110%", "-40°").
export function formatValue(adj, value) {
  const sign = adj.signed && value > 0 ? '+' : '';
  return `${sign}${value}${adj.unit || ''}`;
}
